// src/utils/pendoInit.ts

declare global {
  interface Window {
    pendo?: {
      initialize: (config: {
        visitor: {
          id: string;
          email?: string;
          full_name?: string;
          role?: string;
          [key: string]: unknown;
        };
        account: {
          id: string;
          name?: string;
          [key: string]: unknown;
        };
      }) => void;
      identify: (visitorId: string) => void;
      updateOptions: (options: unknown) => void;
      pageLoad: () => void;
      track: (eventName: string, metadata?: Record<string, unknown>) => void;
      _q?: unknown[];
    };
  }
}

export interface PendoUser {
  id: string;
  email?: string;
  full_name?: string;
  role?: string;
  accountId: string;
  accountName?: string;
  [key: string]: unknown;
}

let pendoScriptLoaded = false;

const isUsableId = (value: string | undefined): value is string =>
  Boolean(value) && value !== 'undefined' && value !== 'null';

/** Pendo's install snippet: queue API calls until the real agent loads. */
const ensurePendoAgentStub = () => {
  if (window.pendo) return;

  const queue: unknown[] = [];
  const agent = {
    _q: queue,
    initialize(...args: unknown[]) {
      queue.unshift(['initialize', ...args]);
    },
    identify(...args: unknown[]) {
      queue.push(['identify', ...args]);
    },
    updateOptions(...args: unknown[]) {
      queue.push(['updateOptions', ...args]);
    },
    pageLoad(...args: unknown[]) {
      queue.push(['pageLoad', ...args]);
    },
    track(...args: unknown[]) {
      queue.push(['track', ...args]);
    },
  };

  window.pendo = agent as unknown as Window['pendo'];
};

export const setupPendo = (user: PendoUser, apiKey: string) => {
  if (!apiKey || !isUsableId(user?.id) || !isUsableId(user?.accountId)) return;

  if (!pendoScriptLoaded) {
    ensurePendoAgentStub();

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://cdn.pendo.io/agent/static/${apiKey}/pendo.js`;
    script.onerror = () => {
      pendoScriptLoaded = false;
    };
    document.head.appendChild(script);
    pendoScriptLoaded = true;
  }

  const { id, email, full_name, role, accountId, accountName, ...rest } = user;

  if (window.pendo && typeof window.pendo.initialize === 'function') {
    window.pendo.initialize({
      visitor: { id, email, full_name, role, ...rest },
      account: { id: accountId, name: accountName },
    });
    return;
  }

  let attempts = 0;
  const maxAttempts = 20;
  const interval = setInterval(() => {
    attempts++;
    if (window.pendo && typeof window.pendo.initialize === 'function') {
      window.pendo.initialize({
        visitor: { id, email, full_name, role, ...rest },
        account: { id: accountId, name: accountName },
      });
      clearInterval(interval);
    } else if (attempts >= maxAttempts) {
      clearInterval(interval);
    }
  }, 500);
};
